import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Download, ChevronDown, ExternalLink, Eye, Copy, Check, Loader2, Sparkles, Bot, Trash2, RotateCw } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { listPendingRequests, listReassignmentEvents, triggerPendingRequestsSync, assignPendingRequest, refreshPendingRequest, deletePendingRequests, aiAssignPendingRequests, extractProducts, latestAiChoiceDemoExpertise, type PendingRequest, type PendingRequestEvent, type AiAssignChoice, type AiAssignRequestPayload, type AiAssignStage } from '../../utils/pendingRequests';
import type { DemoExpertise, ExpertiseLevel } from '../../utils/demoExpertise';
import { trackerGetContributors, trackerGetAvailabilityState, type TrackerAvailabilityState } from '../../utils/tracker';
import { computeRequestSuggestions, countInProgressBySCName, isSuggestionEligible, trimAvailabilityState, type RequestSuggestion } from '../../utils/suggestions';
import { createContributorPseudonymizer, pseudonymizeAvailabilityState, pseudonymizeSuggestion, revealChoice, type ContributorPseudonymizer } from '../../utils/pseudonymize';
import { useRequests } from '../../context/RequestsContext';
import { friendlyTime } from '../../utils/friendlyTime';
const PAGE_SIZE = 50;
const STATUS_LABELS: Record<string, string> = {
  INP: 'In Progress',
  NEW: 'New',
  DN: 'Done',
  HLD: 'Hold',
  CLS: 'Closed',
  CNL: 'Cancelled'
};
function friendlyStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_LABELS[status.toUpperCase()] ?? status;
}
function todayKey(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function DiffBadge() {
  return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-411" data-mp="3itkttt960hb7">
      Differs
    </span>;
}
function ProductsCell({
  row
}: {
  row: PendingRequest;
}) {
  const products = row.products ?? extractProducts(row.rawPayload ?? {});
  if (products.length === 0) return <span className="text-xs text-slate-400" data-id="element-412" data-mp="1ak4xxud10f3f">—</span>;
  return <div className="flex flex-wrap gap-1" data-id="element-413" data-mp="1alptnnb2u42v">
      {products.map(p => <span key={p} className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700" data-id="element-414" data-mp="286u66lybnnxq">
          {p}
        </span>)}
    </div>;
}
function AssigneeCell({
  name,
  highlight
}: {
  name: string | null;
  highlight?: boolean;
}) {
  if (!name) return <span className="text-xs text-slate-400" data-id="element-415" data-mp="1u8nfpqvpcq83">Unassigned</span>;
  return <span className={`text-xs font-medium ${highlight ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-416" data-mp="1uagrtlp342nh">
      {name}
    </span>;
}

// Workfront column: shows every user assigned in Workfront (name + email). Requests
// that became assigned in Workfront move to historical and are listed here.
function WorkfrontCell({
  row
}: {
  row: PendingRequest;
}) {
  const assignees = row.workfrontAssignees ?? [];
  if (assignees.length > 0) {
    return <div className="space-y-0.5" data-id="element-417" data-mp="33fxrmujbp75z">
        {assignees.map(a => <div key={a.email || a.name} className="text-xs text-slate-700" data-id="element-418" data-mp="387fi2onpzpcp">
            <span className="font-medium" data-id="element-419" data-mp="389juxw3ljyp4">{a.name || '—'}</span>
            {a.email && <span className="text-slate-400" data-id="element-420" data-mp="3d6a7wak3ue6b"> · {a.email}</span>}
          </div>)}
      </div>;
  }
  return <AssigneeCell name={row.workfrontAssignee} data-id="element-421" data-mp="3i7znm3i9vg0q" />;
}
function SuggestedSCsCell({
  row,
  suggestionsByRequest
}: {
  row: PendingRequest;
  suggestionsByRequest: Map<string, RequestSuggestion[]> | null;
}) {
  const inProgressLabel = (count: number) => <span className="text-slate-400" data-id="element-422" data-mp="1ruimanb7jynb"> · {count} in progress</span>;
  if (!suggestionsByRequest) return <span className="text-xs text-slate-400" data-id="element-423" data-mp="1wtle3626g8dc">—</span>;
  // Only New, unassigned requests get suggestions.
  if (!isSuggestionEligible(row)) {
    return <span className="text-xs text-slate-400" data-id="element-424" data-mp="11k6twihj2m4d">—</span>;
  }
  const suggestions = suggestionsByRequest.get(row.id) ?? [];
  if (suggestions.length === 0) {
    return <span className="text-xs text-slate-400" data-id="element-425" data-mp="1bf8jfffptq7h">No one available</span>;
  }
  return <div className="space-y-1 max-h-24 overflow-y-auto pr-1" data-id="element-426" data-mp="16ii10lgfuiiw">
      {suggestions.map(sugg => <div key={sugg.contributorId} className="text-[11px] leading-tight" data-id="element-427" data-mp="isxha0cgnh8k">
          <span className={sugg.full ? 'font-medium text-slate-800' : 'font-medium text-amber-700'} data-id="element-428" data-mp="dtb4fs0jo30a">
            {sugg.name}
          </span>
          {!sugg.full && <span className="text-[10px] text-amber-600" data-id="element-429" data-mp="sj0b22reoz7y"> partial</span>}
          <span className="text-slate-300" data-id="element-430" data-mp="nq73760n5arn"> · </span>
          <span className="text-slate-500" data-id="element-431" data-mp="1ee9tifs36j">
            {sugg.matches.filter(m => m.status === 'available').map(m => `${m.product} ${m.level ?? '—'}`).join(' · ')}
          </span>
          {inProgressLabel(sugg.inProgressCount)}
        </div>)}
    </div>;
}
type AiRowState = {
  status: 'loading';
  stage: AiAssignStage;
} | {
  status: 'done';
  choice: AiAssignChoice;
} | {
  status: 'failed';
  error: string;
};
const AI_STAGE_LABELS: Record<AiAssignStage, string> = {
  'loading-agent': 'Loading agent..',
  'fetching-details': 'getting request details...',
  'making-suggestion': 'making suggestion...'
};
function RetryButton({
  onClick
}: {
  onClick: () => void;
}) {
  return <button onClick={onClick} title="Ask AI again for this request" aria-label="Retry AI assign" className="p-0.5 text-slate-400 hover:text-indigo-600 transition-colors" data-id="element-432" data-mp="2ue6fdyxkclwt">
      <RotateCw size={12} data-id="element-433" data-mp="2kg0ffmvbhpul" />
    </button>;
}
function ExpertiseBadge({
  level
}: {
  level: ExpertiseLevel;
}) {
  const tone = level === 'L1' ? 'bg-slate-100 text-slate-600' : level === 'L2' ? 'bg-sky-100 text-sky-700' : level === 'L3' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone}`} data-id="element-434" data-mp="36pgv6xgk3h3z">{level}</span>;
}
function ExpertiseRow({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return <div className="flex items-start gap-2 py-1 text-xs" data-id="element-435" data-mp="2wzxbxqoxll8l">
      <span className="w-32 flex-shrink-0 text-slate-400" data-id="element-436" data-mp="2wzdsu6njoqsz">{label}</span>
      <span className="text-slate-700" data-id="element-437" data-mp="2sz3mutzr60e4">{children}</span>
    </div>;
}
function DemoExpertiseView({
  expertise
}: {
  expertise: DemoExpertise;
}) {
  return <div className="divide-y divide-slate-100" data-id="element-438" data-mp="2izwc8ekekjup">
      <ExpertiseRow label="Description" data-id="element-439" data-mp="2j0nuyoswin3k">{expertise.description}</ExpertiseRow>
      {expertise.products.length > 0 && <ExpertiseRow label="Products" data-id="element-440" data-mp="2e2mbi2rkclrk">
          <span className="inline-flex flex-wrap gap-1" data-id="element-441" data-mp="h89g5ylnlyrd">
            {expertise.products.map(p => <span key={p} className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-id="element-442" data-mp="c8wv9jecf1rf">{p}</span>)}
          </span>
        </ExpertiseRow>}
      {expertise.mode && <ExpertiseRow label="Mode" data-id="element-443" data-mp="orsh3juusco8">{expertise.mode}</ExpertiseRow>}
      {expertise.audience && <ExpertiseRow label="Audience" data-id="element-444" data-mp="tir0isb51qvp">{expertise.audience}</ExpertiseRow>}
      {expertise.businessAsk && <ExpertiseRow label="Business ask" data-id="element-445" data-mp="yhu6ceyy014q">{expertise.businessAsk}</ExpertiseRow>}
      {expertise.timeline && (expertise.timeline.startDate || expertise.timeline.dueDate) && <ExpertiseRow label="Timeline" data-id="element-446" data-mp="1te6nq24cbdw">
          {[expertise.timeline.startDate, expertise.timeline.dueDate].filter(Boolean).join(' → ')}
        </ExpertiseRow>}
      <ExpertiseRow label="Expertise required" data-id="element-447" data-mp="ar81sxo5cyzh">
        <span className="inline-flex items-center gap-1.5" data-id="element-448" data-mp="aulybpa4qdz0">
          <span className="text-[10px] text-slate-400" data-id="element-449" data-mp="fsxba8vq14lg">demo</span>
          <ExpertiseBadge level={expertise.expertiseDemo} data-id="element-450" data-mp="1m7y0lvq8qoj2" />
          <span className="text-[10px] text-slate-400" data-id="element-451" data-mp="1m9ivxnkdqbhz">solution</span>
          <ExpertiseBadge level={expertise.expertiseSolution} data-id="element-452" data-mp="1r4y3xyvphd6h" />
        </span>
      </ExpertiseRow>
    </div>;
}
function SummaryModal({
  request,
  expertise,
  onClose
}: {
  request: PendingRequest;
  expertise: DemoExpertise;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-id="element-453" data-mp="2m0wm8vhy0f4q">
      <div role="dialog" aria-label="Request summary" className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-454" data-mp="2h9eg7t12bocw">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100" data-id="element-455" data-mp="2w0nesrmy0k26">
          <div data-id="element-456" data-mp="2r1sn106zzl5x">
            <h3 className="text-base font-semibold text-slate-900" data-id="element-457" data-mp="34z8xqnwbu13h">{request.name || request.id}</h3>
            <p className="text-xs text-slate-500" data-id="element-458" data-mp="3004jvjygjf6t">{request.id}</p>
          </div>
          <button onClick={onClose} aria-label="Close request summary" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" data-id="element-459" data-mp="2zybdm5rrawzc">
            <Eye size={16} data-id="element-460" data-mp="3h9qfrayc9h4n" />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto" data-id="element-461" data-mp="3ha8hgn3rsnjy">
          <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-462" data-mp="3m7i45uzryl43">Demo expertise</p>
          <DemoExpertiseView expertise={expertise} data-id="element-463" data-mp="38hoh9i7tyax0" />
        </div>
      </div>
    </div>;
}
function AiChoiceCell({
  rowState,
  onPick,
  onRetry,
  demoExpertise
}: {
  rowState: AiRowState | undefined;
  onPick: (choice: AiAssignChoice) => void;
  onRetry: () => void;
  demoExpertise?: DemoExpertise;
}) {
  if (!rowState) return <span className="text-xs text-slate-400" data-id="element-464" data-mp="33jmxsw6hs9l0">—</span>;
  if (rowState.status === 'loading') {
    return <div className="flex items-center gap-1.5" data-id="element-465" data-mp="2pk3qg5li929r">
        <Loader2 size={14} className="animate-spin text-indigo-500" data-id="element-466" data-mp="2tjcin60wcs37" />
        <span className="text-[10px] text-slate-500" data-id="element-467" data-mp="2tlpaqc0dcitz">{AI_STAGE_LABELS[rowState.stage]}</span>
      </div>;
  }
  if (rowState.status === 'failed') {
    return <div className="flex items-center gap-1.5" data-id="element-468" data-mp="rrzg2nmeiwab">
        <span className="text-[10px] text-red-600" title={rowState.error} data-id="element-469" data-mp="13upopt5km0kd">call failed</span>
        <RetryButton onClick={onRetry} data-id="element-470" data-mp="18l625d9e5m6q" />
      </div>;
  }
  return <div className="flex items-center gap-1.5" data-id="element-471" data-mp="18mh7hhihj1zy">
      <span className="group relative inline-block" data-id="element-472" data-mp="twrxqxwbtpo2">
        <button onClick={() => onPick(rowState.choice)} title={rowState.choice.reason} className="text-xs font-medium text-indigo-700 hover:underline whitespace-nowrap" data-id="element-473" data-mp="tu6wob3urdrd">
          {rowState.choice.scName}
        </button>
        {demoExpertise && <span role="tooltip" className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-64 rounded-lg border border-slate-200 bg-white p-2.5 text-left shadow-lg group-hover:block" data-id="element-474" data-mp="1rj8v2352839t">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-475" data-mp="25c44jwcr2oc">
              Demo expertise
            </span>
            {demoExpertise.products.length > 0 && <span className="mt-1 block text-[11px] font-medium text-slate-800" data-id="element-476" data-mp="3usakgof8x3lo">
                {demoExpertise.products.join(', ')}
              </span>}
            {demoExpertise.mode && <span className="mt-0.5 block text-[11px] text-slate-600" data-id="element-477" data-mp="8lvs34tvoig8">{demoExpertise.mode}</span>}
            <span className="mt-1.5 flex items-center gap-1.5" data-id="element-478" data-mp="3lzm501megy1">
              <span className="text-[10px] text-slate-400" data-id="element-479" data-mp="3c22mslubf5cv">demo</span>
              <ExpertiseBadge level={demoExpertise.expertiseDemo} data-id="element-480" data-mp="3c2cbm63mj6gz" />
              <span className="text-[10px] text-slate-400" data-id="element-481" data-mp="37411du9f2ty8">solution</span>
              <ExpertiseBadge level={demoExpertise.expertiseSolution} data-id="element-482" data-mp="3lrm8dz9exdbf" />
            </span>
            {demoExpertise.description && <span className="mt-1.5 block text-[11px] leading-snug text-slate-500" data-id="element-483" data-mp="3h0dxeysfpjw5">
                {demoExpertise.description.length > 120 ? `${demoExpertise.description.slice(0, 120)}…` : demoExpertise.description}
              </span>}
          </span>}
      </span>
      <RetryButton onClick={onRetry} data-id="element-484" data-mp="3dyjg8kq86ti5" />
    </div>;
}
function ExportButton({
  rows
}: {
  rows: PendingRequest[];
}) {
  const handleExport = () => {
    if (rows.length === 0) return;
    const headers = ['ID', 'Name', 'Teams', 'Products', 'Status', 'Entry Date', 'Workfront Assignee', 'Manual Assignment', 'Active'];
    const csvRows = [headers.join(','), ...rows.map(row => [row.id, row.name, row.teams.map(t => t.name).join('; '), (row.products ?? []).join('; '), row.status, row.entryDate, (row.workfrontAssignees ?? []).map(a => a.email ? `${a.name} <${a.email}>` : a.name).join('; ') || (row.workfrontAssignee ?? ''), row.manualAssignment?.scName ?? '', row.inactive ? 'no' : 'yes'].map(v => String(v).includes(',') ? `"${String(v)}"` : String(v)).join(','))];
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pending-requests-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <button onClick={handleExport} disabled={rows.length === 0} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-485" data-mp="1u588ryzl2u2l">
      <Download size={14} data-id="element-486" data-mp="1g4p3gzmi6244" /> Export
    </button>;
}
function EventTimeline({
  events
}: {
  events: PendingRequestEvent[];
}) {
  if (events.length === 0) {
    return <p className="text-sm text-slate-400" data-id="element-487" data-mp="1b75g9a7tuf01">No assignment history.</p>;
  }
  return <div className="space-y-2" data-id="element-488" data-mp="3ahey5jkp3fck">
      {events.map((ev, i) => {
      if (ev.type === 'ai-choice') {
        return <div key={i} className="flex items-start gap-2 text-xs" data-id="element-489" data-mp="35hirfckm59tb">
              <span className="mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700" data-id="element-490" data-mp="3dlmcepxy62uj">
                AI choice
              </span>
              <div className="text-slate-600" data-id="element-491" data-mp="33mwtquy88ed8">
                <span className="font-medium text-slate-800" data-id="element-492" data-mp="33o7xiuro9m4c">{ev.chosen?.scName ?? 'Unassigned'}</span>
                <span className="text-slate-400" data-id="element-493" data-mp="38nb2kf7njscb"> · {friendlyTime(ev.at)}</span>
                {ev.reason && <span className="text-slate-400" data-id="element-494" data-mp="2usidoq5vgzt2"> · {ev.reason}</span>}
                {ev.actorName && <span className="text-slate-400" data-id="element-495" data-mp="2zm39cbjzthp3"> · by {ev.actorName}</span>}
              </div>
            </div>;
      }
      return <div key={i} className="flex items-start gap-2 text-xs" data-id="element-496" data-mp="2pu77cpesd7fl" data-mpi={i}>
            <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${ev.type === 'reassignment' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-497" data-mp="2psm8wopcp4cg">
              {ev.type === 'reassignment' ? 'Workfront' : 'Manual'}
            </span>
            <div className="text-slate-600" data-id="element-498" data-mp="1s9xjdhlolxp4">
              <span className="font-medium text-slate-800" data-id="element-499" data-mp="1sb0clw6h8c87">{ev.from ?? 'Unassigned'}</span>
              {' → '}
              <span className="font-medium text-slate-800" data-id="element-500" data-mp="266khhnpdetwm">{ev.to ?? 'Unassigned'}</span>
              <span className="text-slate-400" data-id="element-501" data-mp="21byf2a19oboo"> · {friendlyTime(ev.at)}</span>
              {ev.actorName && <span className="text-slate-400" data-id="element-502" data-mp="2g59xfaxpi4a8"> · by {ev.actorName}</span>}
            </div>
          </div>;
    })}
    </div>;
}
function CopyButton({
  text,
  label = 'Copy'
}: {
  text: string;
  label?: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text for manual copy
    }
  };
  return <button onClick={e => {
    e.stopPropagation();
    void handleCopy();
  }} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-[11px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors" title={label} aria-label={label} data-id="element-503" data-mp="2twomnzi7j1pq">
      {copied ? <Check size={12} className="text-emerald-500" data-id="element-504" data-mp="25crr8qfgzw05" /> : <Copy size={12} data-id="element-505" data-mp="25ekyo7xwqk97" />}
      {copied ? 'Copied' : label}
    </button>;
}
function RawPayloadView({
  payload
}: {
  payload: Record<string, unknown>;
}) {
  let json: string;
  try {
    json = JSON.stringify(payload, null, 2);
  } catch {
    json = String(payload);
  }
  if (!json || json === '{}' || json === 'undefined') {
    return <p className="text-sm text-slate-400" data-id="element-506" data-mp="2ob44oodezs1j">No raw payload available.</p>;
  }
  return <div data-id="element-507" data-mp="oy9r67qud7zp">
      <div className="flex items-center justify-end mb-1.5" data-id="element-508" data-mp="oyi4u1usghby">
        <CopyButton text={json} label="Copy raw payload" data-id="element-509" data-mp="p0cpdf25e7fi" />
      </div>
      <pre className="text-xs leading-relaxed bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto max-h-80 overflow-y-auto whitespace-pre-wrap break-words" data-id="element-510" data-mp="ty4ey2fblhgj">
        {json}
      </pre>
    </div>;
}
function AccordionSection({
  title,
  defaultOpen = false,
  children
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-id="element-511" data-mp="7qiglw65906h">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-512" data-mp="15bd71ibgsx0x">
        <span data-id="element-513" data-mp="vgcvskdxr0tw">{title}</span>
        <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${open ? '' : '-rotate-90'}`} data-id="element-514" data-mp="rdg8whhd4ik5" />
      </button>
      {open && <div className="px-3 py-2 border-t border-slate-200" data-id="element-515" data-mp="3g5it3hswi093">{children}</div>}
    </div>;
}
function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle
}: {
  label: string;
  options: {
    value: string;
    label: string;
  }[];
  selected: Record<string, boolean>;
  onToggle: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const count = options.filter(o => selected[o.value]).length;
  return <div data-id="element-516" data-mp="1ozmgptvn77w3">
      <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-517" data-mp="1p062xmsbsifx">{label}</label>
      <div ref={ref} className="relative" data-id="element-518" data-mp="1tyqukxtihtsr">
        <button type="button" aria-label={label} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-519" data-mp="1txfnop4rs5xf">
          <span data-id="element-520" data-mp="35koo8lw76w5x">{count > 0 ? `${label} (${count})` : `All ${label.toLowerCase()}s`}</span>
          <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-521" data-mp="35mj4vo0fs247" />
        </button>
        {open && <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto py-1" data-id="element-522" data-mp="2cx2f6y47azbo">
            {options.map(o => <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 cursor-pointer select-none hover:bg-slate-50" data-id="element-523" data-mp="2muq8djm5ryrq">
                <input type="checkbox" checked={!!selected[o.value]} onChange={() => onToggle(o.value)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-524" data-mp="23gaad4lcggh" />
                {o.label}
              </label>)}
            {options.length === 0 && <div className="px-3 py-2 text-xs text-slate-400" data-id="element-525" data-mp="16ab4l6xdv2x9">No options</div>}
          </div>}
      </div>
    </div>;
}
function AssignModal({
  request,
  scOptions,
  scLoading,
  preselectEmail = null,
  onCancel,
  onAssign
}: {
  request: PendingRequest;
  scOptions: {
    name: string;
    email: string;
  }[];
  scLoading: boolean;
  preselectEmail?: string | null;
  onCancel: () => void;
  onAssign: (email: string, name: string) => Promise<void>;
}) {
  const [selected, setSelected] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Contributors load asynchronously after the modal mounts; once they arrive,
  // pre-select the AI's pick (when opening from the AI choice column), falling
  // back to the first contributor. A selection already made is never overridden.
  useEffect(() => {
    if (scOptions.length === 0) return;
    setSelected(prev => prev ?? scOptions.find(o => o.email === preselectEmail) ?? scOptions[0] ?? null);
  }, [scOptions, preselectEmail]);
  const handleAssign = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await onAssign(selected.email, selected.name);
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign');
      setBusy(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel} data-id="element-526" data-mp="1kyb1mquhlrmb">
      <div role="dialog" aria-label="Assign pending request" className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-id="element-527" data-mp="1pwmb30gve040">
        <h3 className="text-base font-semibold text-slate-900 mb-0.5" data-id="element-528" data-mp="1gvwo8m9l7k2f">Assign pending request</h3>
        <p className="text-xs text-slate-500 mb-4" data-id="element-529" data-mp="3il3wx9kulpxp">
          {request.name || request.id} · currently {request.manualAssignment?.scName ?? 'unassigned'}
        </p>
        <div className="flex items-center justify-between mb-1" data-id="element-530" data-mp="3ds0yoo54lsb6">
          <label className="block text-xs font-medium text-slate-500" data-id="element-531" data-mp="38vaje315b0qt">Solutions Consultant</label>
          {scLoading && <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-id="element-532" data-mp="34r2uo5uotksl">
              <Loader2 size={12} className="animate-spin" data-id="element-533" data-mp="5d9458tcmnqm" /> Loading…
            </span>}
        </div>
        <select value={selected?.email ?? ''} onChange={e => {
        const c = scOptions.find(o => o.email === e.target.value);
        setSelected(c ?? null);
      }} disabled={scLoading || scOptions.length === 0} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4 disabled:opacity-50" data-id="element-534" data-mp="cutdj0d4zow">
          {scLoading ? <option value="" data-id="element-535" data-mp="rn8xxp14aj8r">Loading contributors…</option> : scOptions.length === 0 ? <option value="" data-id="element-536" data-mp="wkr3r6ju8icj">No available contributors</option> : scOptions.map(c => <option key={c.email} value={c.email} data-id="element-537" data-mp="10mcg8qsgmum7">{c.name}</option>)}
        </select>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 mb-3" data-id="element-538" data-mp="15fyuuoi6p8kn">{error}</div>}
        <div className="flex items-center justify-end gap-2" data-id="element-539" data-mp="1fbs5218fkpz9">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-540" data-mp="1fa7boexurd2z">
            Cancel
          </button>
          <button onClick={() => void handleAssign()} disabled={busy || scLoading || scOptions.length === 0 || !selected} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-541" data-mp="3m3jdpw4yvlnj">
            Assign
          </button>
        </div>
      </div>
    </div>;
}
export interface PendingRequestsDataSource {
  list: typeof listPendingRequests;
  events: typeof listReassignmentEvents;
  sync: typeof triggerPendingRequestsSync;
  assign: typeof assignPendingRequest;
  refreshRequest: typeof refreshPendingRequest;
  delete: typeof deletePendingRequests;
  aiAssign: typeof aiAssignPendingRequests;
  availability: typeof trackerGetAvailabilityState;
}
const DEFAULT_DATA_SOURCE: PendingRequestsDataSource = {
  list: listPendingRequests,
  events: listReassignmentEvents,
  sync: triggerPendingRequestsSync,
  assign: assignPendingRequest,
  refreshRequest: refreshPendingRequest,
  delete: deletePendingRequests,
  aiAssign: aiAssignPendingRequests,
  availability: trackerGetAvailabilityState
};
export function PendingRequestsTab({
  dataSource = DEFAULT_DATA_SOURCE,
  actor = null,
  onUnassignedCount
}: {
  dataSource?: PendingRequestsDataSource;
  actor?: {
    email: string;
    displayName: string | null;
  } | null;
  onUnassignedCount?: (count: number) => void;
}) {
  const {
    requests: allRequests
  } = useRequests();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Contributor availability (SC suggestions)
  const [availabilityState, setAvailabilityState] = useState<TrackerAvailabilityState | null>(null);
  const [availabilityCheckedAt, setAvailabilityCheckedAt] = useState<string | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  // Manual assignment
  const [assignTarget, setAssignTarget] = useState<PendingRequest | null>(null);
  const [scOptions, setScOptions] = useState<{
    name: string;
    email: string;
  }[]>([]);
  const [scLoading, setScLoading] = useState(false);
  const [assignPreselect, setAssignPreselect] = useState<string | null>(null);

  // AI assign — advisory picks for the currently visible page. Per-request async
  // calls: each eligible request gets its own call, and retry re-asks a single row.
  const [aiRows, setAiRows] = useState<Record<string, AiRowState>>({});
  const [aiSweep, setAiSweep] = useState<{
    targets: string[];
  } | null>(null);
  // Bumped on reload/unmount so late responses from a previous run are ignored.
  const aiGeneration = useRef(0);

  // Bulk delete — permanent, admin-only on the backend
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filters
  const [knownStatuses, setKnownStatuses] = useState<string[]>(Object.keys(STATUS_LABELS));
  const [filterStatuses, setFilterStatuses] = useState<Record<string, boolean>>({});
  const [filterAssignment, setFilterAssignment] = useState<{
    assigned: boolean;
    unassigned: boolean;
  }>({
    assigned: false,
    unassigned: false
  });
  const [search, setSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const assignment: 'assigned' | 'unassigned' | 'all' = filterAssignment.assigned && filterAssignment.unassigned ? 'all' : filterAssignment.assigned ? 'assigned' : filterAssignment.unassigned ? 'unassigned' : 'all';

  // String signature of the selected statuses — a stable primitive for the
  // loadRequests dep. knownStatuses can grow after each fetch (new statuses
  // discovered) without re-triggering a fetch, because the signature only
  // changes when the *selection* changes.
  const statusFilterKey = useMemo(() => knownStatuses.filter(s => filterStatuses[s]).sort().join('|'), [knownStatuses, filterStatuses]);
  const toggleStatusFilter = (status: string) => {
    setFilterStatuses(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
    setOffset(0);
  };
  const toggleAssignmentFilter = (which: 'assigned' | 'unassigned') => {
    setFilterAssignment(prev => ({
      ...prev,
      [which]: !prev[which]
    }));
    setOffset(0);
  };

  // Expanded rows (timeline)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [timelineByRequest, setTimelineByRequest] = useState<Record<string, PendingRequestEvent[]>>({});
  const [timelineLoading, setTimelineLoading] = useState<Record<string, boolean>>({});
  // Demo-expertise parsed during the AI assign sweep/retry (temporary, in-memory).
  const [expertiseByRequest, setExpertiseByRequest] = useState<Record<string, DemoExpertise>>({});
  // Request whose demo-expertise summary is shown in the overlay modal.
  const [summaryRequestId, setSummaryRequestId] = useState<string | null>(null);
  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusIn = statusFilterKey ? statusFilterKey.split('|') : [];
      const data = await dataSource.list({
        offset,
        limit: PAGE_SIZE,
        active: 'all',
        assignment,
        statusIn: statusIn.length > 0 ? statusIn : undefined,
        search: search || undefined,
        entryDateFrom: filterDateFrom || undefined,
        entryDateTo: filterDateTo || undefined
      });
      setRequests(data.requests);
      setTotalCount(data.totalCount);
      setLastFetchedAt(data.lastFetchedAt);
      setExpandedId(null);
      setTimelineByRequest({});
      setTimelineLoading({});
      setExpertiseByRequest({});
      setSummaryRequestId(null);
      setAiRows({});
      setAiSweep(null);
      aiGeneration.current++;
      setSelectedIds(new Set());
      if (data.unassignedCount !== undefined) {
        onUnassignedCount?.(data.unassignedCount);
      }
      setKnownStatuses(prev => {
        const next = [...prev];
        let changed = false;
        for (const r of data.requests) {
          const s = r.status;
          if (s && !next.includes(s)) {
            next.push(s);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [offset, assignment, statusFilterKey, search, filterDateFrom, filterDateTo]);
  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  // Drop any in-flight AI responses after unmount — a stale generation token
  // makes runAiAssign ignore late results.
  useEffect(() => {
    return () => {
      aiGeneration.current++;
    };
  }, []);
  const handleRefresh = async () => {
    setSyncing(true);
    setError(null);
    try {
      await dataSource.sync();
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh pending requests');
    } finally {
      setSyncing(false);
    }
  };
  const handleFetchAvailability = async () => {
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    try {
      const state = await dataSource.availability();
      setAvailabilityState(state);
      setAvailabilityCheckedAt(new Date().toISOString());
    } catch (err) {
      setAvailabilityError(err instanceof Error ? err.message : 'Could not load availability');
    } finally {
      setAvailabilityLoading(false);
    }
  };
  const toggleRow = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!timelineByRequest[id]) {
      setTimelineLoading(prev => ({
        ...prev,
        [id]: true
      }));
      try {
        const events = await dataSource.events(id);
        setTimelineByRequest(prev => ({
          ...prev,
          [id]: events
        }));
      } catch {
        setTimelineByRequest(prev => ({
          ...prev,
          [id]: []
        }));
      } finally {
        setTimelineLoading(prev => ({
          ...prev,
          [id]: false
        }));
      }
    }
  };
  const openAssign = async (request: PendingRequest, preselectEmail?: string) => {
    setAssignTarget(request);
    setAssignPreselect(preselectEmail ?? null);
    setScOptions([]);
    setScLoading(true);
    try {
      const contributors = await trackerGetContributors();
      const options = contributors.filter((c): c is {
        name: string;
        email: string;
      } & typeof c => Boolean(c.name && c.email)).map(c => ({
        name: c.name,
        email: c.email
      })).sort((a, b) => a.name.localeCompare(b.name));
      setScOptions(options);
    } catch {
      setScOptions([]);
    } finally {
      setScLoading(false);
    }
  };
  const handleAssign = async (request: PendingRequest, email: string, name: string) => {
    if (!actor) throw new Error('Unable to identify the signed-in user');
    const assigned = await dataSource.assign(request.id, {
      name,
      email
    }, actor);
    const requestId = assigned.id;
    // Assignment-time refresh: re-fetch just this request's Workfront state so the
    // row reflects reality (assignee + historical/inactive) without a full sync.
    // Best-effort — if the refresh fails the assignment still stands and the row
    // keeps the assign response until the next sync catches up.
    let updated = assigned;
    try {
      updated = await dataSource.refreshRequest(requestId);
    } catch {
      updated = assigned;
    }
    setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
    // A confirmed routing clears that request's AI pick — the column shows "—" again.
    setAiRows(prev => {
      if (!(requestId in prev)) return prev;
      const next = {
        ...prev
      };
      delete next[requestId];
      return next;
    });
    // The temporary demo-expertise accordion is cleared with the pick.
    setExpertiseByRequest(prev => {
      if (!(requestId in prev)) return prev;
      const next = {
        ...prev
      };
      delete next[requestId];
      return next;
    });
    if (expandedId === requestId) {
      try {
        const events = await dataSource.events(requestId);
        setTimelineByRequest(prev => ({
          ...prev,
          [requestId]: events
        }));
      } catch {
        setTimelineByRequest(prev => ({
          ...prev,
          [requestId]: []
        }));
      }
    }
  };
  const buildAiPayload = (row: PendingRequest, suggestions: RequestSuggestion[], p: ContributorPseudonymizer): AiAssignRequestPayload[] => [{
    requestId: row.id,
    name: row.name,
    products: row.products ?? extractProducts(row.rawPayload ?? {}),
    suggestions: suggestions.map(s => pseudonymizeSuggestion(s, p))
  }];

  // Run one AI call for a single request. Each call is self-contained: its own
  // payload and an availability snapshot trimmed to that request's suggested SCs.
  // `restoreOnFailure` keeps the previous pick when a retry fails.
  const runAiAssign = async (row: PendingRequest, state: TrackerAvailabilityState, generation: number, opts: {
    restoreOnFailure?: AiRowState;
    onSettled?: (ok: boolean, error: string | null) => void;
  } = {}) => {
    const today = todayKey();
    const s = computeRequestSuggestions(row, state, today, inProgressBySCName);
    const list = [...s.full, ...s.partial];
    setAiRows(prev => ({
      ...prev,
      [row.id]: {
        status: 'loading',
        stage: 'loading-agent'
      }
    }));
    if (list.length === 0) {
      if (generation !== aiGeneration.current) return;
      setAiRows(prev => ({
        ...prev,
        [row.id]: opts.restoreOnFailure ?? {
          status: 'failed',
          error: 'No eligible suggestions for this request'
        }
      }));
      opts.onSettled?.(false, 'No eligible suggestions');
      return;
    }
    try {
      const trimmed = trimAvailabilityState(state, list.map(x => x.contributorId), today);
      const p = await createContributorPseudonymizer(trimmed.contributors ?? []);
      const payload = buildAiPayload(row, list, p);
      const obfuscatedState = pseudonymizeAvailabilityState(trimmed, p);
      const choices = await dataSource.aiAssign(payload, obfuscatedState, undefined, (requestId, stage, expertise) => {
        if (generation !== aiGeneration.current) return;
        if (requestId !== row.id) return;
        if (expertise) {
          setExpertiseByRequest(prev => ({
            ...prev,
            [row.id]: expertise
          }));
        }
        setAiRows(prev => ({
          ...prev,
          [row.id]: {
            status: 'loading',
            stage
          }
        }));
      }, p.toRevealMap());
      if (generation !== aiGeneration.current) return;
      const rawChoice = choices[0] ?? null;
      const choice = rawChoice ? revealChoice(rawChoice, p) : null;
      setAiRows(prev => ({
        ...prev,
        [row.id]: choice ? {
          status: 'done',
          choice
        } : {
          status: 'failed',
          error: 'No choice returned'
        }
      }));
      opts.onSettled?.(choice !== null, choice ? null : 'No choice returned');
      // The tooltip reads the recorded ai-choice event (reload-safe), so refresh
      // this request's timeline once the pick is persisted.
      if (choice) {
        try {
          const events = await dataSource.events(row.id);
          if (generation !== aiGeneration.current) return;
          setTimelineByRequest(prev => ({
            ...prev,
            [row.id]: events
          }));
        } catch {
          /* tooltip just stays absent until the timeline loads on expand */
        }
      }
    } catch (err) {
      if (generation !== aiGeneration.current) return;
      const message = err instanceof Error ? err.message : 'AI assign failed';
      setAiRows(prev => ({
        ...prev,
        [row.id]: opts.restoreOnFailure && opts.restoreOnFailure.status === 'done' ? opts.restoreOnFailure : {
          status: 'failed',
          error: message
        }
      }));
      opts.onSettled?.(false, message);
    }
  };

  // Initial sweep: one async call per eligible visible request, bounded concurrency.
  // Rows that already have a choice are skipped — per-row retry re-asks them.
  const handleAiAssign = async () => {
    if (!actor) return;
    setError(null);
    let state = availabilityState;
    if (!state) {
      try {
        state = await dataSource.availability();
        setAvailabilityState(state);
        setAvailabilityCheckedAt(new Date().toISOString());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load availability');
        return;
      }
    }
    const generation = aiGeneration.current;
    const today = todayKey();
    const targets: {
      row: PendingRequest;
    }[] = [];
    for (const r of requests) {
      if (!isSuggestionEligible(r)) continue;
      const existing = aiRows[r.id];
      if (existing && existing.status !== 'failed') continue;
      const s = computeRequestSuggestions(r, state, today, inProgressBySCName);
      if (s.full.length + s.partial.length === 0) continue;
      targets.push({
        row: r
      });
    }
    if (targets.length === 0) {
      setError('No eligible requests to assign — AI assign only applies to unassigned requests with status New.');
      return;
    }
    setAiSweep({
      targets: targets.map(t => t.row.id)
    });
    const failures: string[] = [];
    const concurrency = 4;
    let idx = 0;
    const workers = Array.from({
      length: Math.min(concurrency, targets.length)
    }, async () => {
      while (idx < targets.length) {
        const {
          row
        } = targets[idx++];
        await runAiAssign(row, state, generation, {
          onSettled: (ok, error) => {
            if (!ok && error) failures.push(error);
          }
        });
      }
    });
    await Promise.all(workers);
    if (generation !== aiGeneration.current) return;
    setAiSweep(null);
    if (failures.length === targets.length) {
      setError(failures[0] || 'AI assign failed');
    }
  };

  // Re-ask the AI for a single request. On failure the previous choice stays put.
  const retryAiAssign = async (row: PendingRequest) => {
    if (!actor) return;
    const state = availabilityState;
    if (!state) return;
    await runAiAssign(row, state, aiGeneration.current, {
      restoreOnFailure: aiRows[row.id]
    });
  };

  // ─── Bulk selection + permanent delete ──────────────────────────────────────

  const toggleRowSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleAllPage = () => {
    setSelectedIds(prev => {
      const allSelected = requests.length > 0 && requests.every(r => prev.has(r.id));
      const next = new Set(prev);
      for (const r of requests) {
        if (allSelected) next.delete(r.id);else next.add(r.id);
      }
      return next;
    });
  };
  const handleDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await dataSource.delete(ids);
      setConfirmDelete(false);
      setSelectedIds(new Set());
      if (offset > 0 && requests.every(r => selectedIds.has(r.id))) {
        setOffset(Math.max(0, offset - PAGE_SIZE));
      }
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pending requests');
      setConfirmDelete(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  const allPageSelected = requests.length > 0 && requests.every(r => selectedIds.has(r.id));
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // In-progress request count per SC, from the same opportunities/requests
  // dataset ODPanel's SC workload table uses. Keyed by SC display name.
  const inProgressBySCName = useMemo(() => countInProgressBySCName(allRequests), [allRequests]);

  // Suggestions are computed client-side from the cached availability state, per
  // visible row. Availability is only fetched when the button is clicked.
  const suggestionsByRequest = useMemo(() => {
    if (!availabilityState) return null;
    const today = todayKey();
    const map = new Map<string, RequestSuggestion[]>();
    for (const r of requests) {
      if (!isSuggestionEligible(r)) continue;
      const s = computeRequestSuggestions(r, availabilityState, today, inProgressBySCName);
      map.set(r.id, [...s.full, ...s.partial]);
    }
    return map;
  }, [availabilityState, requests, inProgressBySCName]);

  // Progress of the current AI sweep — settled once a targeted row is no longer loading.
  const sweepSettled = useMemo(() => {
    if (!aiSweep) return 0;
    return aiSweep.targets.filter(id => {
      const st = aiRows[id];
      return st && st.status !== 'loading';
    }).length;
  }, [aiSweep, aiRows]);
  const sweepActive = aiSweep !== null && sweepSettled < aiSweep.targets.length;
  const columns: SortableColumn<PendingRequest>[] = [{
    key: 'select',
    label: <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} onClick={e => e.stopPropagation()} aria-label="Select all on page" className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-542" data-mp="c2zdrom9gxdy" />,
    width: '3%',
    align: 'center',
    render: row => <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleRowSelection(row.id)} onClick={e => e.stopPropagation()} aria-label={`Select ${row.id}`} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-543" data-mp="vqf9bqik7u91" />
  }, {
    key: 'name',
    label: 'Request',
    width: '18%',
    render: row => <div className="flex items-center gap-2" data-id="element-544" data-mp="4kjdsc0tbih2">
          <button onClick={() => void toggleRow(row.id)} className="text-slate-400 hover:text-indigo-600 transition-colors flex-shrink-0" title="Toggle history" aria-label={`Toggle history for ${row.name}`} data-id="element-545" data-mp="3vxe2ekjzva6a">
            {expandedId === row.id ? <ChevronDown size={14} data-id="element-546" data-mp="nhubhs6sq3ue" /> : <ChevronRight size={14} data-id="element-547" data-mp="nh1fnm9dkyr1" />}
          </button>
          <div className="min-w-0" data-id="element-548" data-mp="iizv0wwawrdg">
            <div className="text-sm font-medium text-slate-900 truncate" data-id="element-549" data-mp="ik17n5sytlxa">{row.name || '—'}</div>
            <a href={`https://experience.adobe.com/#/@wfadoberm/so:adoberm-Production/workfront/issue/${row.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-indigo-600 hover:underline truncate inline-flex items-center gap-1" title="Open in Workfront" data-id="element-550" data-mp="dj54iugfu3sx">
              {row.id}
              <ExternalLink size={12} className="flex-shrink-0" data-id="element-551" data-mp="19rj8t7z3rolb" />
            </a>
            <div data-id="element-552" data-mp="1drlavi8r2k14">
              <button onClick={() => void toggleRow(row.id)} aria-expanded={expandedId === row.id} className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline" data-id="element-553" data-mp="1dt4thfhvp762">
                <Eye size={12} data-id="element-554" data-mp="1sh9ckkkufkpd" />
                {expandedId === row.id ? 'Hide details' : 'Show details'}
              </button>
              {expertiseByRequest[row.id] && <button onClick={() => setSummaryRequestId(row.id)} className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 transition-colors" data-id="element-555" data-mp="1xhx2t418zxij">
                  <Sparkles size={11} data-id="element-556" data-mp="760w1dghzz2z" />
                  Show request summary
                </button>}
            </div>
          </div>
        </div>,
    sortValue: row => row.name
  }, {
    key: 'products',
    label: 'Products',
    width: '11%',
    render: row => <ProductsCell row={row} data-id="element-557" data-mp="1agwu7gxe2z48" />,
    sortValue: row => (row.products ?? extractProducts(row.rawPayload ?? {})).join(' ')
  }, {
    key: 'suggestions',
    label: 'Available SC',
    width: '16%',
    render: row => <SuggestedSCsCell row={row} suggestionsByRequest={suggestionsByRequest} data-id="element-558" data-mp="1ybzk7s7xj8ma" />
  }, {
    key: 'aiChoice',
    label: 'AI choice',
    width: '11%',
    render: row => <AiChoiceCell rowState={aiRows[row.id]} demoExpertise={latestAiChoiceDemoExpertise(timelineByRequest[row.id] ?? [])} onPick={choice => {
      if (actor) void openAssign(row, choice.scEmail);
    }} onRetry={() => {
      if (actor) void retryAiAssign(row);
    }} data-id="element-559" data-mp="274l1f3vwx790" />,
    sortValue: row => {
      const st = aiRows[row.id];
      return st && st.status === 'done' ? st.choice.scName : '';
    }
  }, {
    key: 'status',
    label: 'Status',
    width: '7%',
    render: row => <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${row.inactive ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-560" data-mp="2lrjrovj19vm5">
          {friendlyStatus(row.status)}
        </span>,
    sortValue: row => row.status
  }, {
    key: 'workfrontAssignee',
    label: 'Workfront',
    width: '7%',
    render: row => <WorkfrontCell row={row} data-id="element-561" data-mp="231aaofil7zae" />,
    sortValue: row => row.workfrontAssignee ?? ''
  }, {
    key: 'manualAssignment',
    label: 'Manual',
    width: '10%',
    render: row => {
      const canAssign = !row.inactive && actor !== null;
      if (!row.manualAssignment) {
        return <div className="flex items-center gap-2" data-id="element-562" data-mp="1y27ap9hgsj9a">
              <AssigneeCell name={null} data-id="element-563" data-mp="1y3s0ymwqxg1c" />
              {canAssign && <button onClick={() => void openAssign(row)} className="text-xs text-indigo-600 hover:underline whitespace-nowrap" data-id="element-564" data-mp="2egybna2vhd48">
                  Assign
                </button>}
            </div>;
      }
      const differs = row.workfrontAssignee !== null && row.workfrontAssignee !== row.manualAssignment.scName;
      return <div className="space-y-0.5" data-id="element-565" data-mp="1vkovmh8euchl">
            <div className="flex items-center gap-1.5" data-id="element-566" data-mp="1qrm2ga7jp51x">
              <AssigneeCell name={row.manualAssignment.scName} highlight={differs} data-id="element-567" data-mp="1qpswyy8o4qvi" />
              {differs && <DiffBadge data-id="element-568" data-mp="1lqoo68pnov5p" />}
              {canAssign && <button onClick={() => void openAssign(row)} className="text-xs text-indigo-600 hover:underline whitespace-nowrap" data-id="element-569" data-mp="1gur4ptj3j8op">
                  Reassign
                </button>}
            </div>
            <span className={`block text-[10px] font-medium ${row.manualAssignment.syncState === 'failed' ? 'text-red-600' : row.manualAssignment.syncState === 'pending-sync' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-570" data-mp="3jgmzlh6o4l0o">
              {row.manualAssignment.syncState}
            </span>
          </div>;
    },
    sortValue: row => row.manualAssignment?.scName ?? ''
  }, {
    key: 'entryDate',
    label: 'Entered',
    width: '14%',
    render: row => <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-571" data-mp="39q1vje886oad">{friendlyTime(row.entryDate)}</span>,
    sortValue: row => row.entryDate
  }];
  return <div className="space-y-4" data-id="element-572" data-mp="3ejd4pm0ifh5g">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-573" data-mp="2h0uxshgli2q6">
        <div data-id="element-574" data-mp="2m0zg8d12d7j1">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-575" data-mp="2m0pks9x9285y">Pending Requests</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-576" data-mp="1vr26thdiju01">
            {totalCount} total · {lastFetchedAt ? `last fetched ${friendlyTime(lastFetchedAt)}` : 'not fetched yet'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5" data-id="element-577" data-mp="1mqdselk7jrmm">
          <div className="flex items-center gap-2" data-id="element-578" data-mp="1mpu7qx36ap4w">
            <ExportButton rows={requests} data-id="element-579" data-mp="1qrfurb9f8jst" />
            <button onClick={() => void handleFetchAvailability()} disabled={availabilityLoading} title="Fetch contributor availability and suggest who can take each unassigned request with status New" className="px-3 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-580" data-mp="1qqe7m8f4s6uq">
              <Sparkles size={14} className={availabilityLoading ? 'animate-pulse' : ''} data-id="element-581" data-mp="u16k4uuybno6" />
              {availabilityLoading ? 'Loading availability…' : 'SC suggestions'}
            </button>
            {actor && <button onClick={() => void handleAiAssign()} disabled={sweepActive} title="Let AI pick one suggested SC per unassigned request with status New on this page" className="px-3 py-1.5 rounded-md border border-indigo-300 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-582" data-mp="fg2gclyr2zh8">
                <Bot size={14} className={sweepActive ? 'animate-pulse' : ''} data-id="element-583" data-mp="ybsjwdmc03x8" />
                {sweepActive ? 'Asking AI…' : 'AI assign'}
              </button>}
            {selectedIds.size > 0 && <button onClick={() => setConfirmDelete(true)} title="Permanently delete the selected requests, their events and history" className="px-3 py-1.5 rounded-md border border-red-300 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors inline-flex items-center gap-1.5" data-id="element-584" data-mp="185hrdbqf0sd5">
                <Trash2 size={14} data-id="element-585" data-mp="9t5ed6gan037" />
                Delete ({selectedIds.size})
              </button>}
            <button onClick={() => void handleRefresh()} disabled={loading || syncing} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-586" data-mp="er6zry16zbhu">
              <RefreshCw size={14} className={loading || syncing ? 'animate-spin' : ''} data-id="element-587" data-mp="skg7zvdelqys" />
              {syncing ? 'Fetching…' : 'Refresh'}
            </button>
          </div>
          {sweepActive && aiSweep && <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-id="element-588" data-mp="16i4nenopc5wg">
              <Loader2 size={12} className="animate-spin" data-id="element-589" data-mp="16fs7sh6f27mk" />
              Asking AI for SC… {sweepSettled} of {aiSweep.targets.length} done
            </span>}
          {availabilityError ? <span className="text-[11px] text-red-600" data-id="element-590" data-mp="30pyqzqdk5xv4">{availabilityError}</span> : availabilityCheckedAt ? <span className="text-[11px] text-slate-500" data-id="element-591" data-mp="2vyqh6t8bgahf">
              Availability checked {friendlyTime(availabilityCheckedAt)}
            </span> : null}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-592" data-mp="2mzlkztk88t8f">
        <div className="grid grid-cols-6 gap-3" data-id="element-593" data-mp="3jokx5jnxmf5w">
          <div data-id="element-594" data-mp="3job57qjbtxxk">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-595" data-mp="3jmqhajripd8o">Search</label>
            <input type="text" value={search} onChange={e => {
            setSearch(e.target.value);
            setOffset(0);
          }} placeholder="Name or OPTASK ID..." className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-596" data-mp="3eqjegzuf4huy" />
          </div>
          <div data-id="element-597" data-mp="1u8ldh8mot7c0">
            <MultiSelectDropdown label="Status" options={knownStatuses.map(s => ({
            value: s,
            label: friendlyStatus(s)
          }))} selected={filterStatuses} onToggle={toggleStatusFilter} data-id="element-598" data-mp="1uc99ceflu5tw" />
            {statusFilterKey === '' && <span className="text-[11px] text-slate-400" data-id="element-599" data-mp="393thfkrxtn58">None selected — showing all</span>}
          </div>
          <div data-id="element-600" data-mp="4rohge47uhlk">
            <MultiSelectDropdown label="Assignment" options={[{
            value: 'assigned',
            label: 'Assigned'
          }, {
            value: 'unassigned',
            label: 'Unassigned'
          }]} selected={{
            assigned: filterAssignment.assigned,
            unassigned: filterAssignment.unassigned
          }} onToggle={v => toggleAssignmentFilter(v as 'assigned' | 'unassigned')} data-id="element-601" data-mp="4r6g532p5d6s" />
            {assignment === 'all' && <span className="text-[11px] text-slate-400" data-id="element-602" data-mp="2gjldhczzodm4">None selected — showing all</span>}
          </div>
          <div data-id="element-603" data-mp="2cys1mhl6x5oq">
            <label className="block text-xs font-medium text-slate-500" data-id="element-604" data-mp="2cyjlmgtrtk9b">
              Entered from
              <input type="date" value={filterDateFrom} onChange={e => {
              setFilterDateFrom(e.target.value);
              setOffset(0);
            }} className="mt-1 block w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-605" data-mp="28wociq5lm2kz" />
            </label>
          </div>
          <div data-id="element-606" data-mp="1parysjaltots">
            <label className="block text-xs font-medium text-slate-500" data-id="element-607" data-mp="1pbbio5jtenag">
              Entered to
              <input type="date" value={filterDateTo} onChange={e => {
              setFilterDateTo(e.target.value);
              setOffset(0);
            }} className="mt-1 block w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-608" data-mp="2407pjza4xu9h" />
            </label>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-609" data-mp="3bk1ug89hu36r">
          {error}
        </div>}

      {/* Table */}
      {loading && requests.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-610" data-mp="3kd55yb0kw5yr">
          Loading pending requests...
        </div> : requests.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-611" data-mp="2slfuwxtmkk4y">
          No pending requests found.
        </div> : <SortableTable columns={columns} data={requests} className="tbl tbl-wrap" defaultSort={{
      key: 'entryDate',
      dir: 'desc'
    }} rowKey={row => row.id} renderExpandedRow={row => expandedId === row.id ? <tr className="bg-slate-50" data-id="element-613" data-mp="gv57pnktreyc">
                <td colSpan={columns.length} className="px-4 py-3" data-id="element-614" data-mp="gxzqzbs3qtq6">
                  <div className="space-y-2" data-id="element-615" data-mp="qzll8aau0jyz">
                    <AccordionSection title="Assignment history" defaultOpen data-id="element-616" data-mp="m10mygf54ad6">
                      {timelineLoading[row.id] ? <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-400" data-id="element-617" data-mp="h98qjp9azaf4">
                          <Loader2 size={16} className="animate-spin" data-id="element-618" data-mp="d7x9i84hvxij" /> Loading history…
                        </div> : <EventTimeline events={timelineByRequest[row.id] ?? []} data-id="element-619" data-mp="879c94poakfv" />}
                    </AccordionSection>
                    <AccordionSection title="Raw payload" defaultOpen data-id="element-620" data-mp="3uhvcyvaua09u">
                      <RawPayloadView payload={row.rawPayload} data-id="element-621" data-mp="3qfi8ea7tw8en" />
                    </AccordionSection>
                  </div>
                </td>
              </tr> : null} data-id="element-612" data-mp="2wlree7a94eeu" />}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-622" data-mp="1olrbjl6h94f9">
          <span data-id="element-623" data-mp="27x5aef0erh9a">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-1" data-id="element-624" data-mp="2c01nxrb3cn6d">
            <button onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-625" data-mp="1y5r7ryd5u1bx">
              <ChevronLeft size={16} data-id="element-626" data-mp="1t88u5uo76xxl" />
            </button>
            <span className="px-2 text-xs" data-id="element-627" data-mp="1t5mltsthgeeb">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of {totalPages}
            </span>
            <button onClick={() => setOffset(o => Math.min((totalPages - 1) * PAGE_SIZE, o + PAGE_SIZE))} disabled={offset >= (totalPages - 1) * PAGE_SIZE} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-628" data-mp="1k6rjirtk7oga">
              <ChevronRight size={16} data-id="element-629" data-mp="3oq22n0e2s5z5" />
            </button>
          </div>
        </div>}

      {/* Permanent delete confirmation */}
      {confirmDelete && <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => !deleteLoading && setConfirmDelete(false)} data-id="element-630" data-mp="1bodqqbzimhb">
          <div role="dialog" aria-label="Delete pending requests" className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-631" data-mp="3spav81xdpxt4">
            <h4 className="text-base font-semibold text-slate-900" data-id="element-632" data-mp="69y9b21mfp3g">Delete {selectedIds.size} request{selectedIds.size === 1 ? '' : 's'}?</h4>
            <p className="mt-2 text-sm text-slate-600" data-id="element-633" data-mp="k8oez62iax3f">
              This permanently deletes the request{selectedIds.size === 1 ? '' : 's'} from the tracker, along with all its events and
              assignment history. Deleted requests will not be re-synced from Workfront. This cannot be undone.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2" data-id="element-634" data-mp="1pelnf06ztm48">
              <button onClick={() => setConfirmDelete(false)} disabled={deleteLoading} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-635" data-mp="1pcqzre4c7fwg">
                Cancel
              </button>
              <button onClick={() => void handleDelete()} disabled={deleteLoading} className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-636" data-mp="16fpya39zrxx9">
                {deleteLoading && <Loader2 size={14} className="animate-spin" data-id="element-637" data-mp="1beklriiwmanp" />}
                {deleteLoading ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>}

      {/* Assign modal */}
      {assignTarget && actor && <AssignModal request={assignTarget} scOptions={scOptions} scLoading={scLoading} preselectEmail={assignPreselect} onCancel={() => setAssignTarget(null)} onAssign={(email, name) => handleAssign(assignTarget, email, name)} data-id="element-638" data-mp="2udid1opt4i6p" />}
      {summaryRequestId && expertiseByRequest[summaryRequestId] && (() => {
      const request = requests.find(r => r.id === summaryRequestId);
      return request ? <SummaryModal request={request} expertise={expertiseByRequest[summaryRequestId]} onClose={() => setSummaryRequestId(null)} data-id="element-639" data-mp="3i2tdf538wakb" /> : null;
    })()}
    </div>;
}